import { useCallback, useMemo } from 'react';

import { buildDashboardFromDraft } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import { useDashboard } from '@/hooks/api/dashboard';
import { useAppSelector } from '@/hooks/redux-hooks';

import {
  selectDashboardName,
  selectDraftTabs,
  selectInitialDashboardName,
  selectOtherDashboardDrafts,
  selectTabDrafts
} from '../../../model/dashboardEdit.selectors';

import type { TabInfo } from '../../menus';

interface UseMoveTargetsParams {
  currentTabId: string | null;
}

interface UseMoveTargetsResult {
  /** Sorted list of all dashboard names (current first) */
  dashboardNamesList: string[];
  /** Effective current dashboard name (draft or initial) */
  effectiveCurrentDashboardName: string;
  /** Get tabs for a specific dashboard, excluding current tab */
  getDashboardTabs: (dashboardName: string) => TabInfo[] | null;
  /** Whether dashboard data is loading */
  isLoading: boolean;
}

/**
 * Hook that provides dashboard and tab targeting logic for move menus.
 * Eliminates code duplication across MoveAppToDashboardMenu and MoveItemToDashboardMenu.
 *
 * Features:
 * - Builds unified dashboard list from API + drafts
 * - Handles draft name changes (removes old name when renamed)
 * - Sorts current dashboard first
 * - Provides tab lookup with current tab exclusion
 *
 * @example
 * ```tsx
 * const { dashboardNamesList, getDashboardTabs } = useMoveTargets({
 *   currentTabId
 * });
 * ```
 */
export const useMoveTargets = ({ currentTabId }: UseMoveTargetsParams): UseMoveTargetsResult => {
  const draftDashboardName = useAppSelector(selectDashboardName);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const draftTabs = useAppSelector(selectDraftTabs);
  const tabDrafts = useAppSelector(selectTabDrafts);
  const otherDashboardDrafts = useAppSelector(selectOtherDashboardDrafts);
  const { data: allDashboards, isLoading } = useDashboard();

  const effectiveCurrentDashboardName = draftDashboardName || initialDashboardName;

  /**
   * Build sorted dashboard list.
   * Includes API dashboards + session drafts.
   * Current dashboard is sorted first.
   */
  const dashboardNamesList = useMemo(() => {
    const names = new Set(Object.keys(allDashboards || {}));

    // Add all dashboards that have drafts in session
    Object.keys(otherDashboardDrafts).forEach(name => names.add(name));
    if (draftDashboardName) names.add(draftDashboardName);
    if (initialDashboardName) names.add(initialDashboardName);

    // If draft name differs from initial, remove the old name
    if (draftDashboardName && draftDashboardName !== initialDashboardName) {
      names.delete(initialDashboardName);
    }

    return Array.from(names).sort((a, b) => {
      // Current dashboard first
      if (a === effectiveCurrentDashboardName) return -1;
      if (b === effectiveCurrentDashboardName) return 1;
      return a.localeCompare(b);
    });
  }, [
    allDashboards,
    otherDashboardDrafts,
    draftDashboardName,
    initialDashboardName,
    effectiveCurrentDashboardName
  ]);

  /**
   * Get tabs for a dashboard.
   * Handles main draft, other drafts, and API data.
   * Excludes current tab from results.
   */
  const getDashboardTabs = useCallback(
    (targetDashboardName: string): TabInfo[] | null => {
      const isMainDraft =
        targetDashboardName === draftDashboardName ||
        (initialDashboardName && targetDashboardName === initialDashboardName);

      let tabs: Record<string, unknown> | null;
      if (isMainDraft) {
        tabs = buildDashboardFromDraft(draftTabs, tabDrafts);
      } else if (otherDashboardDrafts[targetDashboardName]) {
        const draft = otherDashboardDrafts[targetDashboardName];
        tabs = buildDashboardFromDraft(draft.draftTabs, draft.tabDrafts);
      } else {
        tabs = allDashboards?.[targetDashboardName] || null;
      }

      if (!tabs) return null;

      const filteredTabs: TabInfo[] = [];

      for (const [tabName, tabValue] of Object.entries(tabs)) {
        const castedTabValue = tabValue as { id: string } | null;

        let tabId: string | null;
        if (isMainDraft) {
          const draftTab = draftTabs.find(t => t.name === tabName);
          tabId = draftTab?.id || null;
        } else if (otherDashboardDrafts[targetDashboardName]) {
          const draft = otherDashboardDrafts[targetDashboardName];
          const draftTab = draft.draftTabs.find(t => t.name === tabName);
          tabId = draftTab?.id || null;
        } else {
          tabId = castedTabValue?.id || null;
        }

        const finalTabId = tabId || tabName;

        // Exclude current tab
        if (currentTabId && finalTabId === currentTabId && isMainDraft) {
          continue;
        }

        filteredTabs.push({ tabName, tabId: finalTabId });
      }

      return filteredTabs.length > 0 ? filteredTabs : null;
    },
    [
      draftDashboardName,
      initialDashboardName,
      draftTabs,
      tabDrafts,
      otherDashboardDrafts,
      allDashboards,
      currentTabId
    ]
  );

  return { dashboardNamesList, effectiveCurrentDashboardName, getDashboardTabs, isLoading };
};
